import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { Loader2 } from "lucide-react";
import Input from "../form/Input";
import PlacesAutocomplete from "../form/PlacesAutocomplete";
import AddressBook from "../../api/AddressBook";
import { useCustomEventListener } from "react-custom-events";

const initialFormState = {
  name: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  lat: 0,
  lng: 0,
};

export default function AddNewAddressModal({
  open,
  setOpen,
  companyId,
  onSuccess,
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialFormState);
  const [validationErrors, setValidationErrors] = useState(null);
  const [loading, setLoading] = useState(false);

  useCustomEventListener("placeSelected", (data) => {
    setFormData((prev) => ({
      ...prev,
      address: data.addressLine || data.description || "",
      state: data.terms?.[data.terms.length - 2]?.value || "",
      city: data.terms?.[data.terms.length - 3]?.value || "",
      zip: data.zipCode || "",
      lat: data.lat || 0,
      lng: data.lng || 0,
    }));
  });

  useEffect(() => {
    if (open) {
      setFormData(initialFormState);
      setValidationErrors(null);
      setLoading(false);
    }
  }, [open]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    if (validationErrors?.[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleAddAddress = async () => {
    if (!companyId) {
      toast.error(t("locations.assign-a-business"));
      return;
    }

    if (!formData.name.trim() || !formData.address.trim()) {
      setValidationErrors({
        name: !formData.name.trim() ? [t("common.name")] : null,
        address: !formData.address.trim() ? [t("common.address")] : null,
      });
      return;
    }

    setLoading(true);
    setValidationErrors(null);

    const param = {
      name: formData.name.trim(),
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      lat: formData.lat,
      lng: formData.lng,
      company_id: companyId,
    };

    try {
      const response = await AddressBook.add(param);
      toast.success(t("address-book.address-added"));
      if (onSuccess) {
        onSuccess(response.data.data);
      }
      setOpen(false);
    } catch (error) {
      if (error.response?.status === 422) {
        setValidationErrors(error.response.data.errors);
      } else if (error?.response?.status !== 403) {
        toast.error(t("common.something-went-wrong-please-try-again"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-100" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-70 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg p-8 text-center shadow-xl transition-all sm:my-8 sm:p-6 w-full max-w-lg bg-white">
                <div className="mt-2">
                  <h4 className="font-bold text-primary-500 mt-4 mx-6 text-2xl">
                    {t("address-book.add-address")}
                  </h4>
                  <p className="text-sm text-gray-500 mt-2 mb-4">
                    {t(
                      "address-book.add-address-description",
                      "Fill in the information below to add a new address."
                    )}
                  </p>

                  <div className="text-left space-y-4">
                    <Input
                      name="name"
                      label={t("common.name")}
                      type="text"
                      value={formData.name}
                      placeholder={t("common.name")}
                      error={
                        validationErrors?.name ? validationErrors.name[0] : null
                      }
                      onChange={handleChange("name")}
                    />

                    <PlacesAutocomplete
                      label={t("common.address")}
                      placeholder={t("common.address-example")}
                      pValue={formData.address}
                      error={
                        validationErrors?.address
                          ? validationErrors.address[0]
                          : null
                      }
                    />
                  </div>
                </div>

                <div className="mt-8 flex items-center gap-2 sm:px-6">
                  <button
                    type="button"
                    className="flex w-full md:w-1/2 justify-center rounded-xl bg-primary-500 hover:bg-primary-400 px-6 py-2 text-sm font-bold text-white shadow-sm transition-all disabled:opacity-50 items-center gap-2"
                    onClick={handleAddAddress}
                    disabled={loading || !companyId}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t("common.save")
                    )}
                  </button>
                  <button
                    type="button"
                    className="flex w-full md:w-1/2 justify-center rounded-xl bg-primary-500 hover:bg-primary-400 px-6 py-2 text-sm font-bold text-white shadow-sm transition-all disabled:opacity-50"
                    onClick={() => setOpen(false)}
                    disabled={loading}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
